import { LARGE_NUMBER } from '../constant'

/**
 *
 * @param str
 * @returns boolean
 *
 * $ (Dollar: USD, CAD, AUD, etc.)
 * € (Euro)
 * £ (British Pound)
 * ¥ (Japanese Yen, Chinese Yuan)
 * ₹ (Indian Rupee)
 * ₽ (Russian Ruble)
 * ₩ (South Korean Won)
 * ₺ (Turkish Lira)
 * ₴ (Ukrainian Hryvnia)
 * ₱ (Peso)
 * RM (Malaysian Ringgit)
 * R (South African Rand)
 * Kč (Czech Koruna)
 */
export const containsCurrencySymbol = (str: string) => {
  // const currencyRegex = /(?:\b(?:USD|AUD|CAD|RM|R|Kc|Kč)(?=\s?\d))|[$€£¥₹₽₩₺₴₱]/
  const currencyRegex =
    /(?:^|[^A-Za-z])(?:USD|AUD|CAD|RM|R|Kc|Kč)(?=[^A-Za-z]|$)|[$€£¥₹₽₩₺₴₱]/
  return currencyRegex.test(str)
}

export const isPossiblyNumeric = (input: string): boolean => {
  // Allow optional currency, digits, separators, parentheses, spaces
  const numericPattern = /^[^\w]*[-+]?[\d\s.,'()]+[^\w]*$/;
  return numericPattern.test(input.trim());
};

/**
 * Extracts a numeric value from a currency or monetary amount string, handling various international number formats.
 * This function parses currency amounts from different locales and converts them to a JavaScript number.
 *
 * The function intelligently detects and handles:
 * - Different thousands separators (comma, dot, apostrophe, space)
 * - Different decimal separators (dot, comma)
 * - Negative amounts in accounting format with parentheses
 * - Currency symbols and codes (automatically removed)
 * - Various international number formatting conventions
 *
 * Supported input formats:
 * - US format: $1,234.56, (1,234.56), -$1,234.56
 * - European format: €1.234,56, 1.234,56€, (1.234,56)
 * - Swiss format: CHF 1'234'567.89, 1'234.50
 * - French format: 1 234 567,89€, 1 234 567
 * - Mixed formats with spaces: USD 1,234.56, 1 234.56 CAD
 *
 * Format detection logic:
 * - Single dot with 3 digits after: treats as thousands separator (1.000 → 1000)
 * - Single dot with 1-2 digits after: treats as decimal separator (1.50 → 1.5)
 * - Single comma with 3 digits after: treats as thousands separator (1,000 → 1000)
 * - Single comma with 1-2 digits after: treats as decimal separator (1,50 → 1.5)
 * - Multiple separators: uses position and context to determine format
 * - Parentheses: converted to negative sign for accounting-style negatives
 *
 * @param amount - The currency amount string to parse
 * @returns The extracted numeric value as a JavaScript number, or NaN if parsing fails
 *
 * @example
 * ```typescript
 * // US format
 * extractNumericValueFromAmount("$1,234.56")     // 1234.56
 * extractNumericValueFromAmount("($1,234.56)")   // -1234.56
 * extractNumericValueFromAmount("-$1,234")       // -1234
 *
 * // European format
 * extractNumericValueFromAmount("€1.234,56")     // 1234.56
 * extractNumericValueFromAmount("1.234,56€")     // 1234.56
 * extractNumericValueFromAmount("€1.000")        // 1000 (thousands separator)
 *
 * // Swiss format
 * extractNumericValueFromAmount("CHF 1'234.50")  // 1234.5
 * extractNumericValueFromAmount("1'234'567")     // 1234567
 *
 * // French format
 * extractNumericValueFromAmount("1 234 567,89€") // 1234567.89
 * extractNumericValueFromAmount("1 234 567")     // 1234567
 *
 * // Mixed formats
 * extractNumericValueFromAmount("USD 3,333")     // 3333
 * extractNumericValueFromAmount("(USD 3,333)")   // -3333
 * extractNumericValueFromAmount("100.00 CAD")    // 100
 *
 * // Edge cases
 * extractNumericValueFromAmount("€1.50")         // 1.5 (decimal)
 * extractNumericValueFromAmount("$1.000")        // 1000 (thousands)
 * extractNumericValueFromAmount("invalid")       // NaN
 * ```
 */
export const extractNumericValueFromAmount = (amount: string): number => {

  // Handle accounting-style negative (e.g. ($1,234.00) , -$1,234.00, -1,234.00, - USD 100.00, (124.50))
  let raw = amount.trim().replace(/^\((.*)\)$/, '-$1');

  // Keep only useful characters. Remove currency symbols and non-numeric chars EXCEPT "-", ".", ",", "'", "(", ")"
  raw = raw.replace(/([^-]|^)[^\d.,'\s()-]+/g, '$1');

  // Remove remaining unwanted characters, except relevant numeric ones
  let cleaned = raw.replace(/[^\d.,'\s-]/g, '').trim();

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  const hasApostrophe = cleaned.includes("'");
  const hasSpace = cleaned.includes(' ');

  const formatKey = [
    hasDot ? 'dot' : '',
    hasComma ? 'comma' : '',
    hasApostrophe ? 'apostrophe' : '',
    hasSpace ? 'space' : '',
  ].filter(Boolean).join('-');

  switch (formatKey) {
    case 'apostrophe': // Likely Switzerland: "1'234'567" -> 1234567
      cleaned = cleaned.replace(/'/g, '');
      break;

    case 'dot-apostrophe': // Likely Switzerland: "1'234'567.89" -> 1234567.89
      cleaned = cleaned.replace(/'/g, '');
      break;

    case 'comma-apostrophe': // "1'234'567,89" -> 1234567.89
      cleaned = cleaned.replace(/'/g, '').replace(',', '.');
      break;

    case 'dot': // Handle standalone dot - could be thousands separator or decimal
      const dotParts = cleaned.split('.')
      if (dotParts.length === 2) {
        const [, afterDot] = dotParts
        if (afterDot.length === 3) {
          // Likely thousands separator: "1.000" -> 1000
          cleaned = cleaned.replace(/\./g, '')
        }
        // If afterDot.length is 1-2, treat as decimal separator (no change needed)
      } else if (dotParts.length > 2) {
        // Multiple dots, likely thousands separators: "1.234.567" -> 1234567
        cleaned = cleaned.replace(/\./g, '')
      }
      break

    case 'space': // Likely French: "1 234 567" -> 1234567
      cleaned = cleaned.replace(/\s/g, '');
      break;

    case 'comma-space': // Likely French: "1 234 567,89" -> 1234567.89
      cleaned = cleaned.replace(/\s/g, '') // Remove spaces first
      // Apply same logic as 'comma' case to determine if comma is thousands or decimal separator
      const spaceParts = cleaned.split(',')
      if (spaceParts.length === 2) {
        const [, afterComma] = spaceParts
        if (afterComma.length === 3) {
          // Likely a thousands separator, remove the comma
          cleaned = cleaned.replace(/,/g, '')
        } else {
          // Likely a decimal separator
          cleaned = cleaned.replace(',', '.')
        }
      } else {
        // If multiple commas, remove them all (thousands separators)
        cleaned = cleaned.replace(/,/g, '')
      }
      break

    case 'dot-space': // "1 234 567.89" -> 1234567.89
      cleaned = cleaned.replace(/\s/g, '')
      // Apply same logic as 'dot' case to determine if dot is thousands or decimal separator
      const spaceRemoveDotParts = cleaned.split('.')
      if (spaceRemoveDotParts.length === 2) {
        const [, afterDot] = spaceRemoveDotParts
        if (afterDot.length === 3) {
          // Likely thousands separator: "1.000" -> 1000
          cleaned = cleaned.replace(/\./g, '')
        }
        // If afterDot.length is 1-2, treat as decimal separator (no change needed)
      } else if (spaceRemoveDotParts.length > 2) {
        // Multiple dots, likely thousands separators: "1.234.567" -> 1234567
        cleaned = cleaned.replace(/\./g, '')
      }
      break

    case 'dot-comma':
      // Use order of appearance to determine format
      if (cleaned.indexOf(',') < cleaned.indexOf('.')) {
        // Likely US: "1,234.56"
        cleaned = cleaned.replace(/,/g, '')
      } else {
        // Likely EU: "1.234,56"
        cleaned = cleaned.replace(/\./g, '').replace(',', '.')
      }
      break;

    case 'comma': // Likely EU: "234,56" -> 234.56
      const parts = cleaned.split(',');
      if (parts.length === 2) {
        const [, afterComma] = parts;
        if (afterComma.length === 3) {
          // Likely a thousands separator, remove the comma
          cleaned = cleaned.replace(/,/g, '');
        } else {
          // Likely a decimal separator
          cleaned = cleaned.replace(',', '.');
        }
      } else {
        // If multiple commas, remove them all (US-style thousands separators)
        cleaned = cleaned.replace(/,/g, '');
      }
      break;

    case 'dot-comma-space': // Handle both US and EU formats with spaces
      cleaned = cleaned.replace(/\s/g, '') // Remove spaces first
      // Use order of appearance to determine format, same as dot-comma case
      if (cleaned.indexOf(',') < cleaned.indexOf('.')) {
        // Likely US: "1,234.56"
        cleaned = cleaned.replace(/,/g, '')
      } else {
        // Likely EU: "1.234,56"
        cleaned = cleaned.replace(/\./g, '').replace(',', '.')
      }
      break

    default:
      // No separators or unknown format
      cleaned = cleaned.replace(/[\s']/g, '');
      break;
  }

  const number = parseFloat(cleaned);
  return isNaN(number) ? NaN : number;
}

/**
 * Checks if a string contains only currency and numeric components (no additional text).
 * This function validates that the input string represents a currency/numeric value
 * without any surrounding text like labels, descriptions, or other non-monetary content.
 * The string must contain at least one digit to be considered a valid currency/numeric value.
 *
 * Allowed characters:
 * - Currency symbols: $ € £ ¥ ₹ ₽ ₩ ₺ ₴ ₱
 * - Currency codes: USD, AUD, CAD, RM, R, Kč, Kc
 * - Digits: 0-9 (at least one digit required)
 * - Number separators: . (period), , (comma), ' (apostrophe)
 * - Grouping symbols: ( ) (parentheses for negative values)
 * - Signs and spaces: + - (plus/minus signs), space
 *
 * Supported currency formats:
 * - Symbol prefix: $1,234.56, €1.234,67, £-50.25
 * - Symbol suffix: 1234.56$, 1.234,67€
 * - Code prefix: USD 100.00, CAD 1,234.56
 * - Code suffix: 100.00 USD, 1,234.56 CAD
 * - Negative formats: ($100), -$100, (100.00)
 * - Various separators: 1,234.56, 1.234,56, 1'234'567.89, 1 234 567.89
 *
 * @param str - The string to validate
 * @returns true if the string contains only currency/numeric components with at least one digit, false otherwise
 *
 * @example
 * ```typescript
 * isCurrencyOrNumericOnly("$1,234.56")     // true
 * isCurrencyOrNumericOnly("€ 1.234,67")    // true
 * isCurrencyOrNumericOnly("USD 100.00")    // true
 * isCurrencyOrNumericOnly("(123.45)")      // true
 * isCurrencyOrNumericOnly("1'234'567.89")  // true
 * isCurrencyOrNumericOnly("Price: $100")   // false - contains additional text
 * isCurrencyOrNumericOnly("$100 each")     // false - contains additional text
 * isCurrencyOrNumericOnly("Total amount")  // false - no currency/numeric content
 * isCurrencyOrNumericOnly("C - 0451")      // false - C is not a valid currency code
 * isCurrencyOrNumericOnly("$")             // false - no numeric value
 * isCurrencyOrNumericOnly("USD")           // false - no numeric value
 * isCurrencyOrNumericOnly("()")            // false - no numeric value
 * ```
 */
export const isCurrencyOrNumericOnly = (str: string): boolean => {
  const normalizedStr = str.trim().toUpperCase()

  // Return false for empty strings or whitespace-only strings
  if (normalizedStr.length === 0) {
    return false
  }

  const minusNotAtStart = /-/.test(normalizedStr) && !normalizedStr.startsWith('-') && !normalizedStr.includes('(-')
  if (minusNotAtStart) {
    return false
  }

  // Pattern that allows only currency symbols, digits, and formatting characters
  // Currency codes (USD, AUD, etc.) must be followed by space/digit or at word boundaries
  const currencyCodePattern = /\b(USD|AUD|CAD|RM|R|KČ|KC)\b/g
  const basicAllowedPattern = /^[\d$€£¥₹₽₩₺₴₱.,'()\s+-]*$/

  // Remove valid currency codes and check if remaining string contains only allowed characters
  const withoutCurrencyCodes = normalizedStr.replace(currencyCodePattern, '')

  // Check if the pattern is valid
  const isValidPattern = basicAllowedPattern.test(withoutCurrencyCodes)

  if (!isValidPattern) {
    return false
  }

  // Must contain at least one digit to be considered a valid currency/numeric value
  const hasDigit = /\d/.test(normalizedStr)

  return hasDigit
}

export const isLargeNumber = (number: number | string): boolean => {
  const num = typeof number === 'string' ? Number(number.includes("=") ? number.substring(1) : number) : number
  return Math.abs(num) >= LARGE_NUMBER
}